// Settings → Global Roles panel. Lists every (user, global-role)
// grant and lets a roles:manage holder grant or revoke them.
//
// Global roles are the cross-org primitive introduced in 0.1.7:
// emissary / steward / envoy. They live in `global_role_memberships`
// and their capabilities are unioned into the user's effective set
// regardless of per-org membership. See [docs/spec/04 §3.3].
//
// This panel groups the grants by role (emissary's holders, then
// steward's, then envoy's, then any custom global role the operator
// has added). Each row has a revoke button; each role section has
// an "Add user…" affordance.
//
// Server-side auth: GET / POST / DELETE all require roles:manage.
// If the caller doesn't hold it, the queries 403 and we render an
// empty-state with an explanation.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError, apiFetch, apiList, apiSingle } from '../lib/api.js';
import { toast } from './Toast.js';
import { UserPlus, X } from 'lucide-react';
import { UserTypeahead, type UserLite } from './UserTypeahead.js';

interface GlobalGrant {
  userId: string;
  roleId: string;
  roleName: string;
  roleDescription: string | null;
  grantedBy: string | null;
  grantedAt: string;
  user: { id: string; email: string; displayName: string; isActive: boolean };
}

// The three seeded global roles. Listed in priority order so the
// page renders highest-power first. Custom global roles added later
// will fall under an "Other global roles" group.
const KNOWN_GLOBAL_ROLES: { name: string; description: string }[] = [
  { name: 'emissary', description: 'Cross-org platform operator: org lifecycle + members + user provisioning.' },
  { name: 'envoy',    description: 'Backup platform operator. Same powers as emissary, distinct identity.' },
  { name: 'steward',  description: 'Cross-org maintenance + member helper. No org-lifecycle powers.' },
];

export function GlobalRolesPanel() {
  const qc = useQueryClient();
  const [pendingRole, setPendingRole] = useState<string | null>(null);
  const [emailInput, setEmailInput] = useState('');

  const grantsQ = useQuery({
    queryKey: ['global-roles'],
    queryFn: () => apiList<GlobalGrant>('/global-roles'),
  });

  const grantMut = useMutation({
    mutationFn: ({ roleName, email }: { roleName: string; email: string }) =>
      apiSingle<{ userId: string; roleName: string }>('/global-roles', {
        method: 'POST',
        body: { roleName, email },
      }),
    onSuccess: (_d, { roleName, email }) => {
      toast.success(`Granted ${roleName} → ${email}`);
      setPendingRole(null);
      setEmailInput('');
      qc.invalidateQueries({ queryKey: ['global-roles'] });
    },
    onError: (err: unknown) => toast.fromError(err, 'Failed to grant role'),
  });

  const revokeMut = useMutation({
    mutationFn: ({ userId, roleId }: { userId: string; roleId: string }) =>
      apiFetch<void>(`/global-roles/${userId}/${roleId}`, { method: 'DELETE' }),
    onSuccess: (_d, { userId }) => {
      toast.success(`Global role revoked`);
      qc.invalidateQueries({ queryKey: ['global-roles'] });
      void userId;
    },
    onError: (err: unknown) => toast.fromError(err, 'Failed to revoke role'),
  });

  // If the caller can't list grants (no roles:manage), show a clear
  // message instead of a broken-looking empty grid.
  if (grantsQ.isError) {
    const err = grantsQ.error;
    const status = err instanceof ApiError ? err.status : 0;
    if (status === 403) {
      return (
        <div style={{ padding: 20, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', color: 'var(--text-muted)', fontSize: 13 }}>
          Managing global roles requires the <code>roles:manage</code> capability.
          Ask an org owner to grant it, or sign in as a user who holds it.
        </div>
      );
    }
    return <div style={{ color: 'var(--danger-primary, #B3261E)' }}>Failed to load global roles.</div>;
  }

  if (grantsQ.isLoading) {
    return <div style={{ color: 'var(--text-muted)' }}>Loading global roles…</div>;
  }

  const grants = grantsQ.data?.data ?? [];
  // Group grants by role name. Build a Map so unknown roles still surface.
  const byRole = new Map<string, GlobalGrant[]>();
  for (const g of grants) {
    if (!byRole.has(g.roleName)) byRole.set(g.roleName, []);
    byRole.get(g.roleName)!.push(g);
  }

  // Render order: seeded roles first (in KNOWN_GLOBAL_ROLES order),
  // then any custom roles encountered in the grant list.
  const renderOrder: { name: string; description: string }[] = [
    ...KNOWN_GLOBAL_ROLES,
    ...Array.from(byRole.keys())
      .filter((n) => !KNOWN_GLOBAL_ROLES.some((k) => k.name === n))
      .map((n) => ({ name: n, description: 'Custom global role.' })),
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <section style={{ background: 'var(--surface-raised)', padding: 20, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)' }}>
        <h2 style={{ marginTop: 0, fontSize: 16 }}>Global (cross-org) roles</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 0 }}>
          Global roles apply across every organization. A holder's capabilities
          union with whatever their per-org membership grants. Use sparingly —
          emissary in particular can mint, archive, hide, and delete orgs and
          move people between them.
        </p>

        {renderOrder.map((role) => {
          const rows = byRole.get(role.name) ?? [];
          return (
            <div key={role.name} style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border-subtle)' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 4 }}>
                <h3 style={{ margin: 0, fontSize: 14 }}>
                  <code style={{ color: 'var(--accent-primary)', fontWeight: 600 }}>{role.name}</code>
                  <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>
                    {rows.length} holder{rows.length === 1 ? '' : 's'}
                  </span>
                </h3>
                <button
                  type="button"
                  onClick={() => { setPendingRole(role.name); setEmailInput(''); }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '4px 10px',
                    fontSize: 12,
                    border: '1px solid var(--border-strong)',
                    borderRadius: 6,
                    background: 'var(--surface-raised)',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                  }}
                >
                  <UserPlus size={12} /> Add user…
                </button>
              </div>
              <p style={{ margin: '0 0 8px 0', fontSize: 12, color: 'var(--text-muted)' }}>
                {role.description}
              </p>

              {pendingRole === role.name && (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!emailInput.trim()) return;
                    grantMut.mutate({ roleName: role.name, email: emailInput.trim() });
                  }}
                  style={{ display: 'flex', gap: 6, marginBottom: 8, alignItems: 'center' }}
                >
                  {/* CTSK-242 smoke follow-up (2026-07-30): swapped raw
                      email <input> for UserTypeahead (same component
                      MembersPanel uses since CTSK-245). Operators who
                      know the exact email can still type it directly —
                      Enter submits the form; the picker's dropdown is
                      empty-state friendly. The backend `/global-roles`
                      POST still resolves email → userId, so no
                      server-side change was needed. */}
                  <div style={{ flex: 1 }}>
                    <UserTypeahead
                      value={emailInput}
                      onChange={setEmailInput}
                      onSelect={(u: UserLite) => setEmailInput(u.email)}
                      placeholder="Search by name or email…"
                      ariaLabel={`Search users to grant ${role.name}`}
                      autoFocus
                      disabled={grantMut.isPending}
                      inputStyle={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={grantMut.isPending || !emailInput.trim()}
                    style={{
                      padding: '4px 10px',
                      fontSize: 12,
                      background: 'var(--accent-primary)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 4,
                      cursor: grantMut.isPending ? 'wait' : 'pointer',
                    }}
                  >
                    Grant
                  </button>
                  <button
                    type="button"
                    onClick={() => { setPendingRole(null); setEmailInput(''); }}
                    style={{
                      padding: '4px 10px',
                      fontSize: 12,
                      background: 'transparent',
                      color: 'var(--text-muted)',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: 4,
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </form>
              )}

              {rows.length === 0 ? (
                <div style={{ padding: '8px 0', fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  No holders yet.
                </div>
              ) : (
                // Same fixed-layout pattern as MembersPanel — auto-layout
                // was giving the User column too much slack and pinching
                // Email so long addresses wrapped. Fixed + nowrap +
                // ellipsis = clean truncation with full value on hover.
                <table className="grid" style={{ tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: 160 }} />
                    <col style={{ width: 280 }} />
                    <col style={{ width: 180 }} />
                    <col style={{ width: 60 }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Email</th>
                      <th>Granted</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((g) => (
                      <tr key={`${g.userId}|${g.roleId}`}>
                        <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={g.user.displayName}>
                          {g.user.displayName}
                        </td>
                        <td
                          style={{
                            fontFamily: 'var(--font-mono)',
                            fontSize: 12,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={g.user.email}
                        >
                          {g.user.email}
                        </td>
                        <td style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                          {new Date(g.grantedAt).toLocaleString()}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <button
                            type="button"
                            title="Revoke"
                            onClick={() => {
                              if (confirm(`Revoke ${g.roleName} from ${g.user.email}?`)) {
                                revokeMut.mutate({ userId: g.userId, roleId: g.roleId });
                              }
                            }}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 24,
                              height: 24,
                              padding: 0,
                              background: 'transparent',
                              border: '1px solid var(--border-subtle)',
                              borderRadius: 4,
                              color: 'var(--danger-primary, #B3261E)',
                              cursor: 'pointer',
                            }}
                          >
                            <X size={12} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
      </section>

      <section style={{ background: 'var(--surface-sunken)', padding: 16, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)', fontSize: 12, color: 'var(--text-muted)' }}>
        <strong>About these roles</strong>
        <ul style={{ margin: '6px 0 0 16px', padding: 0, lineHeight: 1.6 }}>
          <li><code>emissary</code> — full org lifecycle (create / archive / hide), cross-org member management, user provisioning.</li>
          <li><code>envoy</code> — same caps as emissary; distinct identity for redundancy or emergency-access audit trails.</li>
          <li><code>steward</code> — cross-org member management + user provisioning. NO org-lifecycle powers.</li>
        </ul>
        <p style={{ margin: '8px 0 0 0' }}>
          Per-org owners always manage their own org's members; global roles are for cross-org operators
          (platform admin, on-call rotation, etc.). See <code>docs/spec/04-permissions-rbac.md</code> §4.6–4.8.
        </p>
      </section>
    </div>
  );
}
